import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Link from '@mui/material/Link';
import Button from '@mui/material/Button';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { SxProps, Theme } from '@mui/material/styles';
import type { CSSObject } from '@mui/system';
import { Helmet } from 'react-helmet-async';
import { useRequestPasswordResetMutation } from '#services/auth';
import type { AppConfigState } from '../app/config.slice';

const EMAIL_REGEX = /^\S+@\S+$/;

export default function RequestPasswordReset() {
  const appConfig = useSelector((s: { appConfig: AppConfigState }) => s.appConfig);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const matchMobileWidth = useMediaQuery('(min-width: 450px)', { noSsr: true });
  const [showFullWidth, setShowFullWidth] = useState(true);

  useEffect(() => {
    setShowFullWidth(matchMobileWidth);
  }, [matchMobileWidth]);

  const [requestPasswordReset, { isLoading }] = useRequestPasswordResetMutation();

  const isEmailValid = !email || EMAIL_REGEX.test(email);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!EMAIL_REGEX.test(email)) {
      setError('Invalid e-mail address');
      return;
    }

    void requestPasswordReset(email).then((res) => {
      if (res.error) {
        setError((res.error as { data?: { message?: string } }).data?.message || 'Unable to request a password reset.');
        return;
      }

      setSuccess('If an account exists with this email, a password reset link has been sent.');
    });
  };

  let loginFormPosition: CSSObject = { left: 'calc(50% - 220px)', top: '10%' };
  switch (appConfig.loginFormPosition) {
    case 'top, left':
      loginFormPosition = { left: '30px', top: '30px' };
      break;
    case 'top, right':
      loginFormPosition = { right: '30px', top: '30px' };
      break;
    case 'bottom, left':
      loginFormPosition = { left: '30px', bottom: '30px' };
      break;
    case 'bottom, right':
      loginFormPosition = { right: '30px', bottom: '30px' };
      break;
    default:
      break;
  }

  const loginBgSx: SxProps<Theme> | undefined = appConfig.loginBgImage
    ? {
      backgroundImage: `url("/public/static/images/${appConfig.loginBgImage}")`,
      backgroundSize: 'cover',
      maxWidth: 'unset !important',
      padding: '0 !important',
      height: '100%',
    }
    : undefined;

  const mobileLoginFormStyle: CSSObject = {
    position: 'relative',
    margin: 'auto',
    left: 'unset',
    top: '30px',
  };

  const desktopLoginFormStyle: CSSObject = {
    position: 'absolute',
    ...loginFormPosition,
  };

  return (
    <>
      <Helmet>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Helmet>
      <Container component="main" sx={loginBgSx}>
        <Box sx={{
          maxWidth: 345,
          ...(showFullWidth ? desktopLoginFormStyle : mobileLoginFormStyle),
        }}
        >
        <Card component="form" onSubmit={handleSubmit} noValidate>
          {!appConfig.loginFormHideLogo && (
            <CardMedia
              component="img"
              alt={appConfig.wholesaleName ? `${appConfig.wholesaleName} logo` : 'Logo'}
              height="auto"
              width="360px"
              object-fit="contain"
              image={`/public/static/images/${appConfig.wholesaleLogo ? appConfig.wholesaleLogo : 'default-logo.png'}`}
            />
          )}
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Reset your password
            </Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              value={email}
              error={!isEmailValid}
              helperText={!isEmailValid ? 'Invalid e-mail address' : ''}
              onChange={(event) => setEmail(event.target.value)}
            />
          </CardContent>
          <CardActions sx={{ display: 'block' }}>
            <Button
              type="submit"
              size="small"
              sx={{ ml: '5px', mt: '-10px' }}
              disabled={!email || !isEmailValid}
              loading={isLoading}
            >
              Send reset link
            </Button>
            <Box sx={{ mt: 1 }}>
              <Link component={RouterLink} to="/login" underline="none" sx={{ ml: '5px' }}>
                Back to login
              </Link>
            </Box>
          </CardActions>
        </Card>
        </Box>
      </Container>
    </>
  );
}
